/**
 * features/theme/use-theme.ts — สลับธีมสว่าง/มืด
 *
 * index.html มี inline script อ่าน localStorage['ft-theme'] ก่อน React mount
 * เพื่อกันหน้าจอกระพริบขาวแล้วสลับเป็นมืด (flash of wrong theme)
 * hook นี้ต้องเขียนคีย์เดียวกัน ('ft-theme') และ attribute เดียวกัน (data-theme บน <html>)
 * ไม่งั้นสองที่จะไม่ sync กัน
 */

import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'ft-theme';

function readInitialTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'dark' ? 'dark' : 'light';
}

export function useTheme(): { theme: Theme; toggleTheme: () => void; setTheme: (t: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(readInitialTheme);

  const applyTheme = useCallback((next: Theme) => {
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    applyTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, applyTheme]);

  // ถ้าผู้ใช้เปลี่ยนธีม OS ระหว่างใช้งานอยู่ (และยังไม่เคยเลือกเองในเว็บนี้) ให้ตามไปด้วย
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      applyTheme(event.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [applyTheme]);

  return { theme, toggleTheme, setTheme: applyTheme };
}
